import { ApiClient, type TokenProvider } from './client'
import { DemoClient, IS_DEMO, withDemoPersonas } from './demo'

/**
 * What the pages are allowed to call.
 *
 * Both implementations satisfy this exact type, so a page cannot depend on
 * something only one of them provides — and the build fails if they drift.
 *
 * ---------------------------------------------------------------------------
 * THAT GUARANTEE IS REAL AGAIN AS OF 6 Aug 2026
 *
 * It used to be a lie. `new DemoClient() as Api` told TypeScript to stop
 * checking, and the two had already drifted: DemoClient implemented none of
 * `receivablesTrend`, `receivablesWeeks` or `salespersonReview`, so three of the
 * seven Receivables tabs threw "not a function" in demo mode and rendered the
 * failure panel with a message about an unexpected error.
 *
 * The cast is gone. DemoClient now implements the whole interface — those three
 * by REFUSING with a named reason rather than by inventing twelve weeks of
 * plausible movement, because figures that look considered are the ones a
 * reviewer reasons from.
 */
export type Api = Pick<
  ApiClient,
  'me' | 'actAsCandidates' | 'beginActAs' | 'receivablesMatrix' | 'receivablesTransactions' | 'receivablesDecompose' | 'receivablesDso' | 'receivablesFilterOptions' | 'receivablesWeeks' | 'receivablesTrend' | 'salespersonReview'
  | 'salesOverview' | 'salesExplorer' | 'salesLeaderboard' | 'salesTrend' | 'salesTree' | 'salesFilterOptions'
  | 'salesLines' | 'salesLineFilterOptions'
  | 'currencyRates'
  | 'devFlags' | 'setDevFlag' | 'measureDoc' | 'measureRefs'
  | 'gridLayouts' | 'saveGridLayout' | 'forgetGridLayout' | 'saveReaderSetting'
  | 'readerViews' | 'saveReaderView' | 'deleteReaderView' | 'setDefaultReaderView'
  | 'renameReaderView'
  | 'materialPlanningFilters' | 'materialPlanningOverview' | 'materialPlanningBreakdown'
  | 'materialPlanningStock' | 'materialPlanningPurchaseOrders' | 'materialPlanningShipments'
  | 'materialPlanningSalesVsLy' | 'materialPlanningSalesPerformance'
  | 'materialPlanningItemStatus' | 'materialPlanningItemSummary' | 'materialPlanningItemOrders'
  | 'inventoryAgingFilters' | 'inventoryAgingOverview' | 'inventoryAgingMatrix' | 'inventoryAgingLots'
  | 'statementCompanies' | 'statementCustomers' | 'statement'
  | 'news' | 'publishNews' | 'updateNews' | 'retireNews'
  | 'maintenanceStatus' | 'setMaintenance' | 'clearMaintenance'
  | 'permissionUsers' | 'permissionUserDetail' | 'permissionAnomalies'
  | 'adminReports' | 'reportAccess' | 'grantReportAccess' | 'revokeReportAccess'
  | 'roleHolders' | 'setPlatformRole'
  | 'columnGovernance' | 'setColumnPermissions'
  | 'columnDefinition' | 'columnSettingPreview' | 'columnSettingSave'
  | 'columnGroup' | 'columnGroupCreate' | 'columnGroupSave' | 'columnGroupDelete'
  | 'accessPeople' | 'accessPerson'
  | 'accessApprovals' | 'accessApprovalCard' | 'accessApprove' | 'accessWithdraw'
  | 'accessHrRoles' | 'accessHrRole' | 'accessHrRolePeople' | 'accessHrRoleHistory' | 'accessHrRoleHierarchyPreview' | 'accessHrRolePreview' | 'accessHrRoleSave' | 'accessHrRoleRemove'
  | 'accessProblems' | 'accessProblemItems' | 'accessSync' | 'accessHeldPull' | 'accessAcceptPull'
  | 'accessEmailDomains' | 'accessEmailDomainPreview' | 'accessEmailDomainSave'
  | 'accessStaff' | 'accessHistory' | 'accessOrgCodes' | 'accessPersonSettingsPreview' | 'accessPersonSettingsSave'
  | 'accessExtraScopePreview' | 'accessExtraScopeAdd' | 'accessExtraScopeSave' | 'accessExtraScopeRemove' | 'accessSignInLink'
  | 'accessFindAccount'
>

export function createApi(getToken: TokenProvider): Api {
  // withDemoPersonas lets the demo act as somebody the API would REFUSE, so the
  // refusal path — the one both hooks-order crashes lived on — can be clicked
  // through without a production account. Demo only; the real client is bare.
  return IS_DEMO ? withDemoPersonas(new DemoClient()) : new ApiClient(getToken)
}

export { IS_DEMO }
